// 요금제 카드 — /pricing·랜딩은 무료 + 준비 중(Pro·Max, 가격 없음) · 설정은 가격 없이 한 줄씩.

import { Check } from "lucide-react";

import type { PlanName } from "@buildpy/core/modules/plan/types";
import { Button } from "@buildpy/ui/button";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Link } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { APP_CTA, FREE_LIMITS_LINE, FREE_PLAN, LANDING_FREE_BADGE, LANDING_FREE_POINTS, LANDING_SAME_FEATURES, PREPARING_PLANS, SIGNUP_CTA } from "@/modules/plan/copy";
import { NOTIFY_ME_LABEL, notifyMeHrefOf, PREPARING_LABEL, PREPARING_SHORT_LABEL } from "./plan-cards-state";

interface PricingProps {
  signedIn: boolean;
}

export const PricingPlanCards = ({ signedIn }: PricingProps) => (
  <ul className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="pricing-plans">
    <SectionCard as="li" className="flex flex-col gap-4" data-plan="free" data-testid="pricing-plan-free" padding="lg">
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-2 text-xl font-bold">
          {FREE_PLAN.name}
          <Pill tone="primary">지금 가입하면 이 요금제</Pill>
        </span>
        <span className="text-md text-secondary-foreground">{FREE_PLAN.who}</span>
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-4xl font-bold tabular-nums">{FREE_PLAN.price}</span>
        <span className="text-sm text-muted-foreground">{FREE_PLAN.priceNote}</span>
      </div>
      <ul className="flex flex-col gap-2 text-md">
        {FREE_PLAN.points.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <Check aria-hidden className="mt-1 size-4 shrink-0 text-primary" />
            {p}
          </li>
        ))}
      </ul>
      <div className="mt-auto">
        <Button asChild className="w-full" size="lg">
          <Link data-testid="pricing-free-cta" href={signedIn ? APP_CTA.href : SIGNUP_CTA.href}>
            {signedIn ? APP_CTA.label : SIGNUP_CTA.label}
          </Link>
        </Button>
      </div>
    </SectionCard>
    {PREPARING_PLANS.map((plan) => (
      <SectionCard key={plan.key} as="li" className="flex flex-col gap-4" data-plan={plan.key} data-testid={`pricing-plan-${plan.key}`} padding="lg">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-xl font-bold">
            {plan.name}
            <Pill>{PREPARING_LABEL}</Pill>
          </span>
          <span className="text-md text-secondary-foreground">{plan.who}</span>
        </div>
        <ul className="flex flex-col gap-2 text-md text-secondary-foreground">
          <li>기능 무료와 같음</li>
          <li>사진 저장 · 알림톡 {plan.limit}</li>
          <li>{plan.more}</li>
        </ul>
        <div className="mt-auto">
          <Button asChild className="w-full" size="lg" variant="outline">
            <Link data-testid={`pricing-${plan.key}-notify`} href={notifyMeHrefOf(signedIn)}>
              {NOTIFY_ME_LABEL}
            </Link>
          </Button>
        </div>
      </SectionCard>
    ))}
  </ul>
);

const LANDING_CARD = "flex flex-col p-7";
const LANDING_LIST = "mt-5 flex flex-1 flex-col gap-2.5 border-t border-border pt-5";

const LandingPoint = ({ children }: { children: string }) => (
  <li className="flex gap-2.5 text-lg leading-normal">
    <Check aria-hidden className="mt-1 size-4 shrink-0 text-primary" strokeWidth={2.4} />
    <span>{children}</span>
  </li>
);

const LandingCardHead = ({ name, badge, who, highlight = false }: { name: string; badge: string; who: string; highlight?: boolean }) => (
  <>
    <div className="flex items-center justify-between gap-2">
      <span className="text-3xl font-extrabold tracking-tight">{name}</span>
      <Pill className={cn("px-2.5 py-1 font-bold", highlight && "bg-primary text-primary-foreground")} tone={highlight ? "primary" : "neutral"}>
        {badge}
      </Pill>
    </div>
    <span className="mt-2 text-lg text-secondary-foreground">{who}</span>
  </>
);

// 랜딩 요금 구간(시안 V3) — 무료 카드만 코발트 테두리 · 체크 목록 · 가격 글자 없음.
export const LandingPlanCards = ({ signedIn }: PricingProps) => (
  <ul className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="pricing-plans">
    <SectionCard as="li" className={cn(LANDING_CARD, "border-2 border-primary")} data-plan="free" data-testid="pricing-plan-free" padding="none">
      <LandingCardHead highlight badge={LANDING_FREE_BADGE} name={FREE_PLAN.name} who={FREE_PLAN.who} />
      <ul className={LANDING_LIST}>
        {LANDING_FREE_POINTS.map((p) => (
          <LandingPoint key={p}>{p}</LandingPoint>
        ))}
      </ul>
      <Button asChild className="mt-6 w-full" size="lg">
        <Link data-testid="pricing-free-cta" href={signedIn ? APP_CTA.href : SIGNUP_CTA.href}>
          {signedIn ? APP_CTA.label : SIGNUP_CTA.label}
        </Link>
      </Button>
    </SectionCard>
    {PREPARING_PLANS.map((plan) => (
      <SectionCard key={plan.key} as="li" className={LANDING_CARD} data-plan={plan.key} data-testid={`pricing-plan-${plan.key}`} padding="none">
        <LandingCardHead badge={PREPARING_SHORT_LABEL} name={plan.name} who={plan.who} />
        <ul className={LANDING_LIST}>
          <LandingPoint>{LANDING_SAME_FEATURES}</LandingPoint>
          <LandingPoint>{`사진 저장 ${plan.limit}`}</LandingPoint>
          <LandingPoint>{`알림톡 ${plan.limit}`}</LandingPoint>
        </ul>
        <Button asChild className="mt-6 w-full" size="lg" variant="outline">
          <Link data-testid={`pricing-${plan.key}-notify`} href={notifyMeHrefOf(signedIn)}>
            {NOTIFY_ME_LABEL}
          </Link>
        </Button>
      </SectionCard>
    ))}
  </ul>
);

// 설정 요금제 목록 — 가격 글자 없이 이름·상태·한도만(설정 화면에는 「원」·「결제」 가 없다).
export const SettingsPlanList = ({ current }: { current: PlanName }) => (
  <ul className="flex flex-col divide-y divide-border rounded-md border border-border" data-testid="settings-plan-list">
    <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5" data-plan="free">
      <span className="flex items-center gap-2 text-sm font-semibold">
        {FREE_PLAN.name}
        {current === "free" && <Pill tone="primary">지금 쓰는 중</Pill>}
      </span>
      <span className="text-sm text-secondary-foreground">기능 전부 · {FREE_LIMITS_LINE}</span>
    </li>
    {PREPARING_PLANS.map((plan) => (
      <li key={plan.key} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5" data-plan={plan.key}>
        <span className="flex items-center gap-2 text-sm font-semibold">
          {plan.name}
          {current === plan.key ? <Pill tone="primary">지금 쓰는 중</Pill> : <Pill>준비 중</Pill>}
        </span>
        <span className="text-sm text-secondary-foreground">사진 저장 · 알림톡 {plan.limit}</span>
      </li>
    ))}
  </ul>
);
